/**
 * Admin Client
 * Provides contract-based access to admin/dictionary endpoints.
 * Follows Simple CRUD Pattern from client factory architecture.
 * @module api/clients/admin
 */
import api from "@/services/api"
import { createContractClient } from "../contractClient"
import { ModulesListAnyResponseSchema } from "../contracts/modules"
import { DictResponseSchema } from "../contracts/dicts"
import {
  normalizeModulesList,
  normalizeDict,
} from "../normalizers/admin.normalizer"

// LLM:METADATA
// :hierarchy: [Domain:Admin | API_Client | Factory]
// :relates-to: uses: "createContractClient", uses: "admin normalizers", required-by: "useAppInit.js", "useCommonDicts.js"
// :rationale: "Provides admin API calls with response normalization for 5 admin endpoints (modules list, 4 dictionaries). RAW payload schemas validate transport contracts before normalization."
// :contract: pre: "api client initialized", post: "Client object with modules/dicts methods", invariant: "All responses normalized"
// :decision_cache: "Chose simple client pattern for admin due to low endpoint count (5) and read-only operations. Added RAW-first schema validation for modules and dictionaries [ADMIN-CLIENT-001]"
// :references: SPEC: "Phase 3B Client Factory Implementation", ARCH: "client-factory-architecture.md Pattern 1"
// LLM:END

/**
 * Create admin client with normalization
 * @returns {Object} Admin client with modules and dictionaries methods
 */
export function createAdminClient() {
  // Modules list endpoint client with normalization and schema validation
  const modulesClient = createContractClient(
    api,
    "modules",
    ModulesListAnyResponseSchema,
    normalizeModulesList
  )

  // Order statuses dictionary client with normalization
  const orderStatusesClient = createContractClient(
    api,
    "dict/order_statuses",
    DictResponseSchema,
    normalizeDict
  )

  // Order managers dictionary client with normalization
  const managersClient = createContractClient(
    api,
    "dict/managers",
    DictResponseSchema,
    normalizeDict
  )

  // Payment types dictionary client with normalization
  const paymentTypesClient = createContractClient(
    api,
    "dict/payment_types",
    DictResponseSchema,
    normalizeDict
  )

  // Delivery options dictionary client with normalization
  const deliveryOptionsClient = createContractClient(
    api,
    "dict/delivery_options",
    DictResponseSchema,
    normalizeDict
  )

  return {
    /**
     * Get all modules for app initialization
     *
     * @returns {Promise<ModulesListResponse>} Normalized modules list
     * @returns {Array} returns - Array of normalized module objects
     *
     * @example
     * const modules = await adminClient.getModules()
     * console.log('Loaded modules:', modules.length)
     */
    getModules: async () => {
      return modulesClient.get("")
    },

    /**
     * Get order statuses dictionary
     *
     * @returns {Promise<DictResponse>} Normalized order statuses dictionary
     * @returns {Array} returns - Array of normalized order status items
     *
     * @example
     * const statuses = await adminClient.getOrderStatuses()
     * const activeStatuses = statuses.filter(s => s.active)
     */
    getOrderStatuses: async () => {
      return orderStatusesClient.get("")
    },

    /**
     * Get order managers dictionary
     *
     * @returns {Promise<DictResponse>} Normalized managers dictionary
     * @returns {Array} returns - Array of normalized manager items
     *
     * @example
     * const managers = await adminClient.getOrderManagers()
     * const activeManagers = managers.filter(m => m.active)
     */
    getOrderManagers: async () => {
      return managersClient.get("")
    },

    /**
     * Get payment types dictionary
     *
     * @returns {Promise<DictResponse>} Normalized payment types dictionary
     * @returns {Array} returns - Array of normalized payment type items
     *
     * @example
     * const paymentTypes = await adminClient.getPaymentTypes()
     * const availableTypes = paymentTypes.filter(t => t.active)
     */
    getPaymentTypes: async () => {
      return paymentTypesClient.get("")
    },

    /**
     * Get delivery options dictionary
     *
     * @returns {Promise<DictResponse>} Normalized delivery options dictionary
     * @returns {Array} returns - Array of normalized delivery option items
     *
     * @example
     * const deliveryOptions = await adminClient.getDeliveryOptions()
     * const availableOptions = deliveryOptions.filter(o => o.active)
     */
    getDeliveryOptions: async () => {
      return deliveryOptionsClient.get("")
    },
  }
}

// Singleton instance for direct import
export const adminClient = createAdminClient()
